/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CoreModule
 */

import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AlbHttpClient } from 'ng/modules/http/service/alb-http-client.service';

/**
 * @description Service for downloading file.
 * @author Abhinesh Gour, Shahab Hashmi
 */
@Injectable({
    providedIn: 'root',
})
export class DownloadFileService {
    constructor(private readonly http: AlbHttpClient) {}
    /**
     * Downloads the file as per the given fileName and mimeType.
     * Default mimeType is 'text/json'.
     */
    public downloadFile(
        data: Record<string, string> | string,
        fileName: string,
        mimeType = 'text/json',
    ): void {
        if (!data) {
            throw new Error('Unable to Download, data not available');
        }

        if (typeof data === 'object') {
            data = JSON.stringify(data, undefined, 4);
        }

        const blob = new Blob([data], { type: mimeType });
        const a = document.createElement('a');

        a.download = fileName || 'file';
        a.href = window.URL.createObjectURL(blob);
        a.dataset.downloadurl = [mimeType, a.download, a.href].join(':');
        a.click();
    }

    /**
     * Check whether a resource is accessible at the given URL using a HEAD request.
     * Return true if the server responds with a 2xx status, false otherwise.
     * Avoid downloading the body, making it suitable as a lightweight pre-flight check.
     */
    public async checkFileExists(url: string): Promise<boolean> {
        try {
            await firstValueFrom(this.http.head(url, { observe: 'response' }));

            return true;
        } catch {
            return false;
        }
    }

    /**
     * Download a blob file from the given URL.
     */
    public async downloadBlob(
        url: string,
        fileName: string,
        mimeType = 'application/octet-stream',
    ): Promise<void> {
        const blob = await firstValueFrom(
            this.http.get(url, { responseType: 'blob' }),
        );

        // Create blob with specified mimeType to trigger download
        const downloadBlob = new Blob([blob], { type: mimeType });
        const blobUrl = window.URL.createObjectURL(downloadBlob);
        const a = document.createElement('a');

        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
    }
}
